import React, { useState, useEffect } from 'react';
import { X, ExternalLink, Mail, Check, Gift } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { appUrl } from '../utils/appUrl';
import { useEmailSignup } from '../hooks/useEmailSignup';
import StoreBadges from './StoreBadges';
import { analyticsConsent } from '../utils/analytics';

const STORAGE_KEY = 'recalibrate_popup_dismissed';
const SCROLL_TRIGGER = 0.6;
const FALLBACK_DELAY = 12000;
// Nothing opens the popup before the visitor has been on the page this long.
const MIN_DELAY = 12000;
const CONSENT_RETRY = 3000;
// Article readers get a longer runway: interrupting someone mid-paragraph at 5s
// is the fastest way to lose them. On /blog the popup waits for real reading.
const BLOG_SCROLL_TRIGGER = 0.55;
const BLOG_FALLBACK_DELAY = 60000;

// One popup, one message, on every page.
const COPY = {
  greeting: "Hi, I'm Calum, it's amazing you're here!",
  eyebrow: 'Free starter kit',
  title: 'Get your free Nervous System Reset kit',
  cta: 'Send me my free kit',
};

// Everything listed here is delivered by the welcome email.
const KIT = [
  { title: 'Nervous System Reset starter kit', text: '13-page PDF: the science, 8 daily lifelines and a 7-day plan' },
  { title: '5 regulation practices for today', text: 'Simple, science-backed and free' },
  { title: 'Free app access', text: 'Nervous-system assessment on iPhone, Android or web' },
];

export default function CalumPopup() {
  const location = useLocation();
  const pathname = location?.pathname || '/';
  const variant = COPY;

  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  const { email, setEmail, loading, error, handleEmailSubmit } = useEmailSignup({
    name: 'Calum Popup',
    onSuccess: () => {
      setSubmitted(true);
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'sign_up', { method: 'calum_popup', event_category: 'engagement' });
        window.gtag('event', 'generate_lead', {
          event_category: 'engagement',
          event_label: 'calum_popup_email',
          method: 'calum_popup_email',
        });
      }
    },
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (localStorage.getItem(STORAGE_KEY)) return undefined;

    let fired = false;
    let retry;
    const landedAt = Date.now();
    const isBlog = pathname.startsWith('/blog');
    const scrollTrigger = isBlog ? BLOG_SCROLL_TRIGGER : SCROLL_TRIGGER;
    const fallbackDelay = isBlog ? BLOG_FALLBACK_DELAY : FALLBACK_DELAY;

    const open = () => {
      if (fired || Date.now() - landedAt < MIN_DELAY) return;
      // The cookie choice decides the popup: declined means it never shows, and while
      // the visitor hasn't chosen yet we wait rather than cover the cookie banner.
      const consent = analyticsConsent();
      if (consent === 'denied') { cleanup(); return; }
      if (consent !== 'granted' || document.querySelector('[data-testid="analytics-consent"]')) {
        clearTimeout(retry);
        retry = setTimeout(open, CONSENT_RETRY);
        return;
      }
      fired = true;
      cleanup();
      setShow(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimateIn(true)));
    };

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= scrollTrigger) open();
    };

    // Desktop exit-intent: cursor leaves the top of the viewport.
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) open();
    };

    // Fallback timer guarantees the popup appears on mobile, where there may
    // be no mouse-leave and a short page that never hits 25% scroll quickly.
    const fallback = setTimeout(open, fallbackDelay);

    function cleanup() {
      clearTimeout(fallback);
      clearTimeout(retry);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onMouseOut);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseout', onMouseOut);

    return cleanup;
  }, [pathname]);

  const handleClose = () => {
    setAnimateIn(false);
    setTimeout(() => {
      setShow(false);
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    }, 350);
  };

  if (!show) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-350 ${animateIn ? 'bg-black/40 backdrop-blur-[2px]' : 'bg-transparent'}`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      data-testid="calum-popup-overlay"
    >
      <div
        className={`relative w-full max-w-md transition-all duration-350 ease-out ${animateIn ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'}`}
        data-testid="calum-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Card */}
        <div className="relative max-h-[calc(100dvh-2rem)] overflow-y-auto bg-gradient-to-br from-slate-900 via-purple-950 to-indigo-950 rounded-3xl border border-purple-500/20 shadow-2xl shadow-purple-900/40">
          {/* Ambient glow */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500/15 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-36 h-36 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

          {/* Close */}
          <button onClick={handleClose} className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-testid="calum-popup-close">
            <X className="w-4 h-4 text-white/70" />
          </button>

          <div className="relative p-6 sm:p-8">
            {/* Calum greeting */}
            <div className="flex items-center gap-3 mb-5 pr-8">
              <div className="w-14 h-14 shrink-0 rounded-2xl overflow-hidden border-2 border-purple-400/30 shadow-lg shadow-purple-500/20">
                <img src="/calum-dragon.jpg" alt="Calum" className="w-full h-full object-cover" data-testid="calum-popup-image" />
              </div>
              <p className="text-purple-200/85 text-sm leading-snug">
                {submitted ? 'Thanks, you are on the list.' : variant.greeting}
              </p>
            </div>

            {submitted ? (
              <div>
                <h3 className="text-white text-2xl font-extrabold leading-tight mb-2" data-testid="calum-popup-title">You're in!</h3>
                <p className="text-purple-200/70 text-sm leading-relaxed mb-4" data-testid="calum-popup-success-copy">
                  Your free starter kit is on its way. Check your inbox in the next few minutes.
                </p>
                <a
                  href={appUrl('/', 'calum-popup-success-app-root', 'popup')}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="calum-popup-go-to-app"
                  className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3.5 rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-purple-600/30 transition-all hover:-translate-y-0.5"
                >
                  Open the app <ExternalLink className="w-4 h-4" />
                </a>
                <div className="mt-5" data-testid="calum-popup-store-badges">
                  <StoreBadges align="center" web={false} testidPrefix="popup-store" />
                </div>
              </div>
            ) : (
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 text-amber-300 ring-1 ring-amber-300/30 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] mb-3">
                  <Gift className="w-3 h-3" /> {variant.eyebrow}
                </span>
                <h3 className="text-white text-2xl sm:text-[1.7rem] font-extrabold leading-tight mb-4" data-testid="calum-popup-title">
                  {variant.title}
                </h3>

                <div className="flex gap-4 sm:gap-5 mb-5">
                  <picture className="shrink-0 w-[5.5rem] sm:w-28 self-start">
                    <source srcSet="/starter-kit-cover.webp" type="image/webp" />
                    <img
                      src="/starter-kit-cover.jpg"
                      alt="Nervous System Reset starter kit cover"
                      width="320"
                      height="453"
                      loading="lazy"
                      data-testid="calum-popup-kit-cover"
                      className="w-full h-auto rounded-lg shadow-xl shadow-black/40 ring-1 ring-white/15 -rotate-3"
                    />
                  </picture>
                  <ul className="space-y-3" data-testid="calum-popup-kit">
                    {KIT.map(({ title, text }) => (
                      <li key={title} className="flex items-start gap-2.5">
                        <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-emerald-400/20 ring-1 ring-emerald-300/40 flex items-center justify-center">
                          <Check className="w-3 h-3 text-emerald-300" strokeWidth={3} />
                        </span>
                        <span className="text-[13px] leading-snug text-purple-100/70">
                          <b className="block text-sm text-white font-semibold">{title}</b>{text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <form onSubmit={handleEmailSubmit} className="space-y-3" data-testid="calum-popup-email-form">
                  {error && <p role="alert" data-testid="calum-popup-signup-error" className="text-sm text-red-200">{error}</p>}
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    required
                    data-testid="calum-popup-email"
                    className="w-full bg-white border border-purple-300 rounded-xl px-4 py-3.5 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-400/30 transition-all"
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="calum-popup-email-signup"
                    className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-xl font-bold text-base hover:shadow-lg hover:shadow-purple-600/30 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {loading ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    ) : (
                      <><Mail className="w-4 h-4" /> {variant.cta}</>
                    )}
                  </button>
                  <p className="text-center text-[11px] text-purple-200/60" data-testid="calum-popup-reassurance">Free &middot; No card needed &middot; Lands in your inbox in minutes</p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
